import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ExternalDataService,
  GooglePlaceTypeDto,
  CollectMarketingPlacesRequestDto,
  MarketingPlaceJobStatusDto,
  MarketingPlaceJobStatus
} from '../../../services/externalDataService.service';
import { AuthService } from '../../../services/auth.service';
import { PermissionService } from '../../../services/permission.service';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { filter, take, takeUntil, Subject } from 'rxjs';

@Component({
  selector: 'app-marketing-place-collector',
  standalone: true,
  imports: [CommonModule, FormsModule, HasPermissionDirective],
  templateUrl: './marketing-place-collector.component.html',
  styleUrl: './marketing-place-collector.component.css'
})
export class MarketingPlaceCollectorComponent implements OnInit, OnDestroy {
  // Place types
  placeTypes: GooglePlaceTypeDto[] = [];
  loadingPlaceTypes = false;

  // Group place types by category
  get groupedPlaceTypes(): { category: string; types: GooglePlaceTypeDto[] }[] {
    const grouped = new Map<string, GooglePlaceTypeDto[]>();

    this.placeTypes.forEach(type => {
      const category = type.category || 'Other';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(type);
    });

    // Convert to array and sort by category name
    return Array.from(grouped.entries())
      .map(([category, types]) => ({ category, types }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }

  // Form data
  formData: CollectMarketingPlacesRequestDto = {
    placeType: '',
    latitude: undefined,
    longitude: undefined,
    radiusMeters: undefined,
    query: '',
    fetchAllPages: true,
    forceRefresh: true
  };

  // Job status
  jobStatus: MarketingPlaceJobStatusDto | null = null;
  currentJobId: string | null = null;

  loading = false;
  checkingStatus = false;
  errorMessage = '';
  successMessage = '';

  private destroy$ = new Subject<void>();

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService,
    public permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    // Wait for MSAL to be initialized before loading place types
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadPlaceTypes();
        this.loadJobIdFromStorage();
      });
  }

  loadPlaceTypes(): void {
    this.loadingPlaceTypes = true;
    this.externalDataService.getPlaceTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (types) => {
          this.placeTypes = types || [];
          this.loadingPlaceTypes = false;
        },
        error: (error) => {
          console.error('Error loading place types:', error);
          this.loadingPlaceTypes = false;
          this.errorMessage = 'Failed to load place types. Please try again.';
        }
      });
  }

  loadJobIdFromStorage(): void {
    const storedJobId = localStorage.getItem('marketingPlaceJobId');
    if (storedJobId) {
      this.currentJobId = storedJobId;
      // Optionally load the job status on init
      // this.checkJobStatus();
    }
  }

  saveJobIdToStorage(jobId: string): void {
    this.currentJobId = jobId;
    localStorage.setItem('marketingPlaceJobId', jobId);
  }

  clearJobIdFromStorage(): void {
    this.currentJobId = null;
    localStorage.removeItem('marketingPlaceJobId');
    this.jobStatus = null;
  }

  startCollection(): void {
    // Validate required fields
    if (!this.formData.placeType || !this.formData.placeType.trim()) {
      this.errorMessage = 'Please select a Place Type.';
      return;
    }

    if (this.formData.latitude === undefined || this.formData.longitude === undefined) {
      this.errorMessage = 'Please provide both Latitude and Longitude.';
      return;
    }

    if (this.formData.radiusMeters === undefined || this.formData.radiusMeters <= 0) {
      this.errorMessage = 'Please provide a valid Radius in meters (greater than 0).';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request: CollectMarketingPlacesRequestDto = {
      placeType: this.formData.placeType.trim(),
      latitude: this.formData.latitude,
      longitude: this.formData.longitude,
      radiusMeters: this.formData.radiusMeters,
      query: this.formData.query?.trim() || undefined,
      fetchAllPages: this.formData.fetchAllPages || false,
      forceRefresh: this.formData.forceRefresh || false
    };

    this.externalDataService.collectMarketingPlaces(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.jobStatus = response;
          this.saveJobIdToStorage(response.id);
          this.successMessage = `Job started successfully! Job ID: ${response.id}`;
        },
        error: (error) => {
          console.error('Error starting collection job:', error);
          this.loading = false;

          if (error.status === 400) {
            this.errorMessage = error.error?.detail || 'Invalid request. Please check your input.';
          } else if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to start collection jobs.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to start collection job. Please try again.';
          }
        }
      });
  }

  checkJobStatus(): void {
    if (!this.currentJobId || !this.currentJobId.trim()) {
      this.errorMessage = 'No job ID available. Please start a new collection job.';
      return;
    }

    this.checkingStatus = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.externalDataService.getMarketingPlaceJobStatus(this.currentJobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.checkingStatus = false;
          this.jobStatus = response;
          this.successMessage = 'Job status updated.';
        },
        error: (error) => {
          console.error('Error checking job status:', error);
          this.checkingStatus = false;

          if (error.status === 404) {
            this.errorMessage = 'Job not found. It may have expired or been deleted.';
            this.clearJobIdFromStorage();
          } else if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to view job status.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to check job status. Please try again.';
          }
        }
      });
  }

  getStatusBadgeClass(status: MarketingPlaceJobStatus): string {
    switch (status) {
      case MarketingPlaceJobStatus.Completed:
        return 'status-badge status-completed';
      case MarketingPlaceJobStatus.Running:
        return 'status-badge status-running';
      case MarketingPlaceJobStatus.Failed:
        return 'status-badge status-failed';
      case MarketingPlaceJobStatus.Cancelled:
        return 'status-badge status-cancelled';
      case MarketingPlaceJobStatus.Pending:
      default:
        return 'status-badge status-pending';
    }
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

